import { motion, AnimatePresence } from "framer-motion";
import { Case } from "@/types/workflow";
import { CaseSearchSection } from "./CaseSearchSection";
import { CasesTableSection } from "../CasesTableSection";
import { WorkflowSection } from "../WorkflowSection";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSidebar } from "@/components/ui/sidebar/context";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface CaseContentSectionProps {
  selectedCase: Case | null;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filteredCases: Case[];
  isLoading: boolean;
  error: Error | null;
  onEdit: (caseData: Case) => void;
  onDelete: (caseData: Case) => void;
  onViewWorkflow: (caseData: Case) => void;
  onWorkflowUpdate: () => Promise<void>;
  onBack: () => void;
}

export function CaseContentSection({
  selectedCase,
  searchTerm,
  onSearchChange,
  filteredCases,
  isLoading,
  error,
  onEdit,
  onDelete,
  onViewWorkflow,
  onWorkflowUpdate,
  onBack
}: CaseContentSectionProps) {
  const { open: sidebarOpen } = useSidebar();
  const isMobile = useIsMobile();

  return (
    <motion.div 
      className={cn(
        "flex-1 bg-card rounded-lg border shadow-sm overflow-hidden",
        "transition-all duration-300 ease-in-out min-h-0",
        "w-full"
      )}
      layout
    >
      <AnimatePresence mode="wait">
        {selectedCase ? (
          <motion.div
            key="workflow"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            <WorkflowSection
              selectedCase={selectedCase}
              onWorkflowUpdate={onWorkflowUpdate}
              onBack={onBack}
            />
          </motion.div>
        ) : (
          <motion.div
            key="cases"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col h-full"
          >
            <div className="p-2 sm:p-4 flex-none">
              <CaseSearchSection 
                searchTerm={searchTerm}
                onSearchChange={onSearchChange}
              />
            </div>

            <ScrollArea className="flex-1 min-h-0 w-full">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : error ? (
                <div className="p-4">
                  <Alert variant="destructive">
                    <AlertDescription>
                      Det oppstod en feil ved lasting av saker: {error.message}
                    </AlertDescription>
                  </Alert>
                </div>
              ) : (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-2 sm:p-4 w-full"
                >
                  <CasesTableSection
                    cases={filteredCases}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onViewWorkflow={onViewWorkflow}
                  />
                </motion.div>
              )}
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}